// utils/datetime.js
// Shared date/time helpers for the Site Visit module (and anywhere else).
//
// ZENREAL ESTATES CRM only operates in India, so every date/time in the app is
// always meant to be interpreted and displayed in IST (Asia/Kolkata, UTC+5:30,
// no DST) — never "whatever timezone this browser/device happens to be set to".
// Relying on the device's local timezone (via `getTimezoneOffset()` or
// `toLocaleString` without an explicit `timeZone`) silently breaks the moment a
// user's laptop/phone clock is misconfigured (wrong TZ, wrong region, a stale
// VM clock, etc.) — the exact class of bug that showed a visit booked for
// 12:00 PM being saved/displayed as 2:00 PM. Pinning the offset explicitly
// makes the app's date handling independent of that.
const IST_OFFSET_MINUTES = 330; // Asia/Kolkata is a fixed UTC+5:30, year-round

// Convert a date/ISO string into the value a <input type="datetime-local">
// expects, showing the IST wall-clock time of that instant — regardless of
// the browser/device's own timezone setting.
export const toDateTimeLocal = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const istShifted = new Date(dt.getTime() + IST_OFFSET_MINUTES * 60000);
  return istShifted.toISOString().slice(0, 16);
};

// Inverse of toDateTimeLocal: takes a <input type="datetime-local"> value
// (e.g. "2026-08-22T12:00") and treats those digits literally as IST
// wall-clock time, returning the correct UTC ISO string to save — again,
// independent of the browser/device's own timezone setting.
export const fromDateTimeLocal = (localStr) => {
  if (!localStr) return null;
  const [datePart, timePart = "00:00"] = localStr.split("T");
  const [y, m, day] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if (!y || !m || !day) return null;
  const utcMs = Date.UTC(y, m - 1, day, hh || 0, mm || 0) - IST_OFFSET_MINUTES * 60000;
  return new Date(utcMs).toISOString();
};

// Human-readable date + time in IST, e.g. "5 Aug 2026, 05:30 PM" — pinned to
// Asia/Kolkata explicitly so it never drifts with the viewer's device timezone.
export const formatDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

// Compact date + time in IST, e.g. "Aug 5, 05:30 PM" — for tight dashboard/list UI.
export const formatShortDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).replace(",", ",");
};

// Time only, e.g. "05:30 PM" — pinned to IST.
export const formatTimeOnly = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

// The Y/M/D of an instant AS SEEN IN IST — use this instead of getFullYear()/
// getMonth()/getDate() (which use the device's local timezone) whenever you
// need to bucket a date by its IST calendar day (e.g. calendar grids, "same
// day" filters). Returns 1-indexed month.
export const getISTDateParts = (d) => {
  const dt = new Date(d);
  const istShifted = new Date(dt.getTime() + IST_OFFSET_MINUTES * 60000);
  return {
    year: istShifted.getUTCFullYear(),
    month: istShifted.getUTCMonth() + 1,
    day: istShifted.getUTCDate(),
  };
};

// "YYYY-MM-DD" of an instant as seen in IST — safe to compare against a plain
// <input type="date"> value, unlike `new Date(d).toISOString().slice(0,10)`
// (which uses UTC and can land on the wrong calendar day near midnight IST).
export const toISTDateString = (d) => {
  if (!d) return "";
  const { year, month, day } = getISTDateParts(d);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};
